import { CircleAlert, FlaskRound, Rocket, Wrench, X } from 'lucide-react'
import { type JSX, useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import useTranslator from '@/hooks/translation'

import {
  type BotEnvironment,
  ENV_BADGE_CLASSNAME,
  ENV_TRANSLATION_KEY,
  getAvailableTargets,
  getBotEnvironment,
  getDefaultTarget,
} from '../migration-create-dialog/utils/bot-environment'

const ICON_BY_ENV: Record<BotEnvironment, JSX.Element> = {
  dev: <Wrench />,
  stg: <FlaskRound />,
  prd: <Rocket />,
}

interface MigrationPreselectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceBotName: string
  onContinue: (targetEnv: BotEnvironment) => void
}

export function MigrationPreselectDialog({
  open,
  onOpenChange,
  sourceBotName,
  onContinue,
}: MigrationPreselectDialogProps) {
  const { getTranslation } = useTranslator()

  const sourceEnv = getBotEnvironment(sourceBotName)
  const availableTargets = getAvailableTargets(sourceEnv)
  const defaultTarget = getDefaultTarget(sourceEnv)

  const [selectedEnv, setSelectedEnv] = useState<BotEnvironment>(defaultTarget)

  useEffect(() => {
    if (open) setSelectedEnv(defaultTarget)
  }, [open, defaultTarget])

  const sourceEnvLabel = getTranslation(ENV_TRANSLATION_KEY[sourceEnv])
  const targetEnvLabel = getTranslation(ENV_TRANSLATION_KEY[selectedEnv])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeButton={false}
        className="max-w-[720px] gap-0 overflow-hidden rounded-2xl border border-[rgba(86,83,134,0.17)] bg-white p-0 shadow-xl"
      >
        <header className="flex h-14 items-center justify-between border-b border-[rgba(86,83,134,0.17)] bg-white py-4 pl-8 pr-6">
          <DialogTitle className="text-lg font-bold text-[#333]">
            {getTranslation('botMigration.dialogs.preselect.title')}
          </DialogTitle>

          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6 text-[#7c8d9f] hover:bg-transparent [&_svg]:size-5"
              aria-label={getTranslation(
                'botMigration.dialogs.preselect.close',
              )}
            >
              <X />
            </Button>
          </DialogClose>
        </header>

        <div className="flex flex-col gap-8 border-l border-r border-[rgba(86,83,134,0.17)] bg-white px-8 py-12">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-bold leading-7 text-[#0f1217]">
              {getTranslation('botMigration.dialogs.preselect.heading')}
            </h2>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-base">
              <span className="text-[#737882]">
                {getTranslation(
                  'botMigration.dialogs.preselect.summary.migrating',
                )}
              </span>
              <span className="text-[#0f1217]">{sourceBotName}</span>
              <span className="text-[#737882]">
                {getTranslation('botMigration.dialogs.preselect.summary.from')}
              </span>
              <Badge className={ENV_BADGE_CLASSNAME[sourceEnv]}>
                {sourceEnvLabel}
              </Badge>
              <span className="text-[#737882]">
                {getTranslation('botMigration.dialogs.preselect.summary.to')}
              </span>
            </div>
          </div>

          <ToggleGroup
            type="single"
            variant="fintalk"
            size="xl"
            value={selectedEnv}
            onValueChange={(value) => {
              if (value) setSelectedEnv(value as BotEnvironment)
            }}
            className="w-full items-stretch gap-2"
          >
            {availableTargets.map((env) => (
              <ToggleGroupItem
                key={env}
                value={env}
                icon={ICON_BY_ENV[env]}
                text={getTranslation(ENV_TRANSLATION_KEY[env])}
                className="flex-1"
              />
            ))}
          </ToggleGroup>

          <div
            role="alert"
            className="flex w-full flex-col gap-2 rounded-xl border border-[#fed7aa] bg-[#fff7ed] p-4 text-[#ea580c]"
          >
            <div className="relative w-full pl-7">
              <CircleAlert
                aria-hidden
                className="absolute left-0 top-1 size-4"
                strokeWidth={2}
              />
              <p className="text-base font-semibold leading-6">
                {getTranslation(
                  'botMigration.dialogs.preselect.warning.title',
                  { targetEnv: targetEnvLabel },
                )}
              </p>
            </div>
            <div className="w-full pl-7 text-sm">
              <p className="leading-[1.6]">{`•  ${getTranslation(
                'botMigration.dialogs.preselect.warning.bullet1',
              )}`}</p>
              <p className="leading-[1.6]">{`•  ${getTranslation(
                'botMigration.dialogs.preselect.warning.bullet2',
                { targetEnv: targetEnvLabel },
              )}`}</p>
              <p className="leading-[1.6]">{`•  ${getTranslation(
                'botMigration.dialogs.preselect.warning.bullet3',
              )}`}</p>
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-4 border-t border-[rgba(86,83,134,0.17)] bg-[#f6f6f8] py-5 pl-4 pr-8">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {getTranslation('botMigration.dialogs.preselect.cancel')}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="default"
            onClick={() => onContinue(selectedEnv)}
          >
            {getTranslation('botMigration.dialogs.preselect.continue')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
